import React, { useState, useEffect } from "react"
import { useFormikContext } from "formik"
import FormikWrapper from "~/library/FormikWrapper"
import FormikInput from "~/library/FormikInput"
import FormikSelect from "~/library/FormikSelect"
import FormikTextarea from "~/library/FormikTextarea"
import LoadingWrapper from "~/library/LoadingWrapper"
import getAppConfig from "~/utils/getAppConfig"
import { apiWorkspaceAccounts, apiWorkspaceContacts, apiWorkspaceDeals } from "~/routes"
import * as Yup from "yup"

const taskSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    description: Yup.string(),
    taskType: Yup.string().required("Task type is required"),
    status: Yup.string().required("Status is required"),
})

interface TaskFormProps {
    onSubmit: (values: any) => void
    onCancel: () => void
    initialValues?: any
    isEditing?: boolean
    presetAccountId?: string
    presetContactId?: string
    presetDealId?: string
}


const TaskFormFields: React.FC<{
    allAccounts: any[]
    allContacts: any[]
    allDeals: any[]
    loadingAccounts: boolean
    loadingContacts: boolean
    loadingDeals: boolean
    isStandalone: boolean
    isInsideAccount: boolean
    isInsideContact: boolean
    isInsideDeal: boolean
    presetAccountInternalId: string
    presetContactInternalId: string
    taskTypeOptions: any[]
    statusOptions: any[]
    onCancel: () => void
    isEditing: boolean
}> = ({
          allAccounts,
          allContacts,
          allDeals,
          loadingAccounts,
          loadingContacts,
          loadingDeals,
          isStandalone,
          isInsideAccount,
          isInsideContact,
          isInsideDeal,
          presetAccountInternalId,
          presetContactInternalId,
          taskTypeOptions,
          statusOptions,
          onCancel,
          isEditing
      }) => {
    const { setFieldValue, values } = useFormikContext<any>()

    const handleAccountChange = (accountId: string) => {
        setFieldValue('accountId', accountId)

        if (values.dealId) {
            const deal = allDeals.find(d => d.id === values.dealId)
            if (!deal || (accountId && deal.account?.id !== accountId)) {
                setFieldValue('dealId', '')
            }
        }

        if (values.contactId) {
            const contact = allContacts.find(c => c.id === values.contactId)
            if (!contact || (accountId && contact.account?.id !== accountId)) {
                setFieldValue('contactId', '')
            }
        }
    }

    const handleContactChange = (contactId: string) => {
        setFieldValue('contactId', contactId)

        if (contactId) {
            const contact = allContacts.find(c => c.id === contactId)
            if (contact?.account?.id) {
                setFieldValue('accountId', contact.account.id)

                if (values.dealId) {
                    const deal = allDeals.find(d => d.id === values.dealId)
                    if (deal && deal.account?.id !== contact.account.id) {
                        setFieldValue('dealId', '')
                    }
                }
            }
        }
    }

    const handleDealChange = (dealId: string) => {
        setFieldValue('dealId', dealId)

        if (dealId) {
            const deal = allDeals.find(d => d.id === dealId)
            if (deal?.account?.id) {
                setFieldValue('accountId', deal.account.id)

                if (values.contactId) {
                    const contact = allContacts.find(c => c.id === values.contactId)
                    if (contact && contact.account?.id !== deal.account.id) {
                        setFieldValue('contactId', '')
                    }
                }
            }
        }
    }

    const getAvailableContacts = () => {
        if (!values.accountId) return allContacts
        return allContacts.filter(contact => contact.account?.id === values.accountId)
    }

    const getAvailableDeals = () => {
        if (!values.accountId) return allDeals
        return allDeals.filter(deal => deal.account?.id === values.accountId)
    }

    const accountOptions = allAccounts.map(acc => ({ value: acc.id, label: acc.name }))

    const contactOptions = getAvailableContacts().map(contact => ({
            value: contact.id,
            label: `${contact.firstName} ${contact.lastName}`
        }))

    const dealOptions = getAvailableDeals().map(deal => ({ value: deal.id, label: deal.name }))

    return (
        <>
            <FormikInput
                name="title"
                label="Title"
                placeholder="Enter task title"
                required
                helpText=""
            />

            <FormikTextarea
                name="description"
                label="Description"
                placeholder="Enter task description (optional)"
                rows={8}
                maxLength={1200}
            />

            {isStandalone && (
                <>
                    <FormikSelect
                        name="accountId"
                        label="Account"
                        options={accountOptions}
                        disabled={loadingAccounts}
                        helpText=""
                        onChange={(e) => handleAccountChange(e.target.value)}
                    />

                    <FormikSelect
                        name="dealId"
                        label="Deal"
                        options={dealOptions}
                        disabled={loadingDeals}
                        onChange={(e) => handleDealChange(e.target.value)}
                    />

                    <FormikSelect
                        name="contactId"
                        label="Contact"
                        options={contactOptions}
                        disabled={loadingContacts}
                        onChange={(e) => handleContactChange(e.target.value)}
                    />
                </>
            )}

            {isInsideAccount && (
                <>
                    <div className="mb-3">
                        <label className="form-label">Account</label>
                        <input
                            type="text"
                            className="form-control"
                            value={allAccounts.find(a => a.id === presetAccountInternalId)?.name || "Loading..."}
                            disabled
                        />
                        <small className="text-muted">This task will be associated with this account</small>
                    </div>

                    <FormikSelect
                        name="dealId"
                        label="Deal"
                        options={dealOptions}
                        disabled={loadingDeals}
                        helpText=""
                        onChange={(e) => handleDealChange(e.target.value)}
                    />

                    <FormikSelect
                        name="contactId"
                        label="Contact"
                        options={contactOptions}
                        disabled={loadingContacts}
                        helpText=""
                        onChange={(e) => handleContactChange(e.target.value)}
                    />
                </>
            )}

            {isInsideContact && (
                <>
                    <div className="mb-3">
                        <label className="form-label">Contact</label>
                        <input
                            type="text"
                            className="form-control"
                            value={
                                allContacts.find(c => c.id === presetContactInternalId)
                                    ? `${allContacts.find(c => c.id === presetContactInternalId)?.firstName} ${allContacts.find(c => c.id === presetContactInternalId)?.lastName}`
                                    : "Loading..."
                            }
                            disabled
                        />
                        <small className="text-muted">This task will be associated with this contact</small>
                    </div>

                    {presetAccountInternalId && (
                        <div className="mb-3">
                            <label className="form-label">Account</label>
                            <input
                                type="text"
                                className="form-control"
                                value={allAccounts.find(a => a.id === presetAccountInternalId)?.name || ""}
                                disabled
                            />
                            <small className="text-muted">This contact's account</small>
                        </div>
                    )}

                    <FormikSelect
                        name="dealId"
                        label="Deal"
                        options={dealOptions}
                        disabled={loadingDeals}
                        helpText={presetAccountInternalId ? "Filtered by contact's account" : ""}
                        onChange={(e) => handleDealChange(e.target.value)}
                    />
                </>
            )}

            {isInsideDeal && (
                <>
                    <div className="alert alert-info mb-3">
                        This task will be associated with the current deal
                    </div>

                    {presetAccountInternalId && (
                        <div className="mb-3">
                            <label className="form-label">Account</label>
                            <input
                                type="text"
                                className="form-control"
                                value={allAccounts.find(a => a.id === presetAccountInternalId)?.name || ""}
                                disabled
                            />
                            <small className="text-muted">This deal's account</small>
                        </div>
                    )}

                    <FormikSelect
                        name="contactId"
                        label="Contact"
                        options={contactOptions}
                        disabled={loadingContacts}
                        helpText="Select a contact from this deal's account"
                        onChange={(e) => handleContactChange(e.target.value)}
                    />
                </>
            )}

            <div className="row">
                <div className="col-6">
                    <FormikSelect
                        name="taskType"
                        label="Task Type"
                        options={taskTypeOptions}
                        required
                        helpText=""
                        noEmptyOption={true}
                    />
                </div>
                <div className="col-6">
                    <FormikSelect
                        name="status"
                        label="Status"
                        options={statusOptions}
                        required
                        helpText=""
                        noEmptyOption={true}
                    />
                </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-3">
                <button type="button" className="btn btn-secondary" onClick={onCancel}>
                    Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                    {isEditing ? "Update Task" : "Create Task"}
                </button>
            </div>
        </>
    )
}

const TaskForm: React.FC<TaskFormProps> = ({
                                               onSubmit,
                                               onCancel,
                                               initialValues,
                                               isEditing = false,
                                               presetAccountId,
                                               presetContactId,
                                               presetDealId,
                                           }) => {
    const { venueSlug } = getAppConfig()

    const [presetAccountInternalId, setPresetAccountInternalId] = useState<string>("")
    const [presetContactInternalId, setPresetContactInternalId] = useState<string>("")
    const [presetDealInternalId, setPresetDealInternalId] = useState<string>("")

    const [allAccounts, setAllAccounts] = useState<any[]>([])
    const [allContacts, setAllContacts] = useState<any[]>([])
    const [allDeals, setAllDeals] = useState<any[]>([])

    const [loadingAccounts, setLoadingAccounts] = useState(false)
    const [loadingContacts, setLoadingContacts] = useState(false)
    const [loadingDeals, setLoadingDeals] = useState(false)
    const [initialLoadComplete, setInitialLoadComplete] = useState(false)

    const isStandalone = !presetAccountId && !presetContactId && !presetDealId
    const isInsideAccount = !!presetAccountId
    const isInsideContact = !!presetContactId
    const isInsideDeal = !!presetDealId

    const defaultValues = {
        title: "",
        description: "",
        taskType: "follow_up",
        status: "not_started",
        accountId: "",
        contactId: "",
        dealId: "",
    }

    const taskTypeOptions = [
        { value: "call", label: "Call" },
        { value: "meeting", label: "Meeting" },
        { value: "follow_up", label: "Follow Up" },
        { value: "proposal", label: "Proposal" },
        { value: "renewal", label: "Renewal" },
    ]

    const statusOptions = [
        { value: "not_started", label: "Not Started" },
        { value: "in_progress", label: "In Progress" },
        { value: "completed", label: "Completed" },
        { value: "cancelled", label: "Cancelled" },
    ]

    useEffect(() => {
        const loadData = async () => {
            if (isStandalone) {
                await Promise.all([fetchAllAccounts(), fetchAllContacts(), fetchAllDeals()])
            } else if (isInsideAccount) {
                await Promise.all([
                    fetchAccountDetails(presetAccountId!),
                    fetchAllAccounts(),
                    fetchAllContacts(),
                    fetchAllDeals()
                ])
            } else if (isInsideContact) {
                await fetchContactDetails(presetContactId!)
                await fetchAllAccounts()
                await fetchAllDeals()
            } else if (isInsideDeal) {
                await fetchDealDetails(presetDealId!)
                await fetchAllAccounts()
                await fetchAllContacts()
            }
            setInitialLoadComplete(true)
        }
        loadData()
    }, [])

    const fetchAllAccounts = async () => {
        setLoadingAccounts(true)
        try {
            const response = await fetch(apiWorkspaceAccounts.index.path({ venue_slug: venueSlug }))
            const data = await response.json()
            setAllAccounts(data.accounts || [])
        } finally {
            setLoadingAccounts(false)
        }
    }

    const fetchAccountDetails = async (accountPrefixId: string) => {
        try {
            const response = await fetch(apiWorkspaceAccounts.show.path({ venue_slug: venueSlug, id: accountPrefixId }))
            const data = await response.json()
            setPresetAccountInternalId(data.account.id)
        } catch (error) {
            console.error("Error fetching account:", error)
        }
    }

    const fetchAllContacts = async () => {
        setLoadingContacts(true)
        try {
            const response = await fetch(apiWorkspaceContacts.index.path({ venue_slug: venueSlug }))
            const data = await response.json()
            setAllContacts(data.contacts || [])
        } finally {
            setLoadingContacts(false)
        }
    }

    const fetchAllDeals = async () => {
        setLoadingDeals(true)
        try {
            const response = await fetch(apiWorkspaceDeals.index.path({ venue_slug: venueSlug }))
            const data = await response.json()
            setAllDeals(data.deals || [])
        } finally {
            setLoadingDeals(false)
        }
    }

    const fetchContactDetails = async (contactPrefixId: string) => {
        try {
            const response = await fetch(apiWorkspaceContacts.show.path({ venue_slug: venueSlug, id: contactPrefixId }))
            const data = await response.json()
            setPresetContactInternalId(data.contact.id)
            if (data.contact?.account) {
                setPresetAccountInternalId(data.contact.account.id)
            }
        } catch (error) {
            console.error("Error fetching contact:", error)
        }
    }

    const fetchDealDetails = async (dealPrefixId: string) => {
        try {
            const response = await fetch(apiWorkspaceDeals.show.path({ venue_slug: venueSlug, id: dealPrefixId }))
            const data = await response.json()
            setPresetDealInternalId(data.deal.id)
            if (data.deal?.account) {
                setPresetAccountInternalId(data.deal.account.id)
            }
        } catch (error) {
            console.error("Error fetching deal:", error)
        }
    }

    const getFormInitialValues = () => {
        if (isEditing) return initialValues
        return {
            ...defaultValues,
            accountId: presetAccountInternalId,
            contactId: presetContactInternalId,
            dealId: presetDealInternalId,
        }
    }

    const handleSubmit = (formValues: any) => {
        const enrichedValues = {
            ...formValues,
            accountId: formValues.accountId || presetAccountInternalId,
            contactId: formValues.contactId || presetContactInternalId,
            dealId: formValues.dealId || presetDealInternalId,
        }
        onSubmit(enrichedValues)
    }

    if (!initialLoadComplete) {
        return <LoadingWrapper isLoading={true} />
    }

    return (
        <FormikWrapper
            onSubmit={handleSubmit}
            validationSchema={taskSchema}
            initialValues={getFormInitialValues()}
            enableReinitialize={true}
        >
            <TaskFormFields
                allAccounts={allAccounts}
                allContacts={allContacts}
                allDeals={allDeals}
                loadingAccounts={loadingAccounts}
                loadingContacts={loadingContacts}
                loadingDeals={loadingDeals}
                isStandalone={isStandalone}
                isInsideAccount={isInsideAccount}
                isInsideContact={isInsideContact}
                isInsideDeal={isInsideDeal}
                presetAccountInternalId={presetAccountInternalId}
                presetContactInternalId={presetContactInternalId}
                taskTypeOptions={taskTypeOptions}
                statusOptions={statusOptions}
                onCancel={onCancel}
                isEditing={isEditing}
            />
        </FormikWrapper>
    )
}

export default TaskForm
